import React, { useState, useEffect } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { AttachMoney } from '@mui/icons-material'
import { ContentCopy } from 'mdi-material-ui'
import moment from 'moment'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { getBilledOutAmountHistory } from 'src/services/LookupService'
import { formatCurrency } from 'src/utils/Formatter'
import ReactPaginate from 'react-paginate'

interface GridDataItem {
  episodeOfCareNumber: string
  groupNumber: string
  insuranceCompany: string
  placeOfService: string
  cptCode: string
  modifiers: string
  quantity: number
  dateBilledOut: string
  billedOutAmount: number
}

interface IBillOutHistoryModalProps {
  close: () => void
  isOpen: boolean
  cptCode: string
  providerTypeId: string
  providerGroupId: string
  onCopy?: (amount: number) => void
  summary: any
  selectedCms1500Id: string
}

const BillOutHistoryModal: React.FC<IBillOutHistoryModalProps> = ({
  close,
  isOpen,
  cptCode,
  providerTypeId,
  providerGroupId,
  onCopy,
  summary,
  selectedCms1500Id,
}) => {
  const selectedClaim = summary?.claims?.find(
    (claim: { cms1500Id: string }) => claim.cms1500Id === selectedCms1500Id
  )
  const [gridData, setGridData] = useState<GridDataItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(5)
  const [pageCount, setPageCount] = useState(1)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)
  const [totalItems, setTotalItems] = useState(0)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      const data = {
        providerGroupId,
        cptCode,
        providerTypeId,
        page,
        pageSize,
      }

      const response = await getBilledOutAmountHistory(data)
      setGridData(response.items)
      setPageCount(response.totalPages ?? 1)
      setTotalItems(response.totalItems ?? 0)
      setLoading(false)
      setPaginationOffsetData(response.totalItems ?? 0)
    }

    fetchData()
  }, [cptCode, providerGroupId, providerTypeId, page, pageSize])

  const setPaginationOffsetData = (totalItems: number) => {
    setFirstRecordIndex((page - 1) * pageSize + 1)
    setLastRecordIndex(page * pageSize < totalItems ? page * pageSize : totalItems)
  }

  const handlePageChange = (event: any) => {
    setPage(event.selected + 1)
  }

  const mapLogGridData = () => {
    if (totalItems === 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={10} align="center">
            No previous payments
          </TableCell>
        </TableRow>
      )
    } else {
      return gridData.map((record: any, index: number) => (
        <TableRow key={index}>
          <TableCell>
            {record.episodeOfCareNumber}-{record.groupNumber}
          </TableCell>
          <TableCell>{record.insuranceCompany}</TableCell>
          <TableCell>{record.placeOfService}</TableCell>
          <TableCell>{record.cptCode}</TableCell>
          <TableCell>{record.modifiers}</TableCell>
          <TableCell align="right">{record.quantity}</TableCell>
          <TableCell align="right">
            {moment(record.dateBilledOut).format('MM/DD/YYYY')}
          </TableCell>
          <TableCell align="right">
            {formatCurrency(record.billedOutAmount.toString())}
          </TableCell>
          <TableCell>
            <ContentCopy
              color="primary"
              style={{ cursor: 'pointer' }}
              onClick={() => copy(record.billedOutAmount)}
            />
          </TableCell>
        </TableRow>
      ))
    }
  }

  const copy = (billedOutAmount: number) => {
    if (onCopy) {
      onCopy(billedOutAmount)
    }
    close()
  }

  const paginationInfo = () => {
    if (totalItems !== 0) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }
    return null
  }

  return (
    <Dialog fullWidth={true} maxWidth="lg" open={isOpen}>
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar
          title={`Billed Out Amount History: ${selectedClaim?.providerGroup}`}
          pageIcon={<AttachMoney />}
          isModal={true}
        />
      </DialogTitle>
      {loading && (
        <div
          style={{
            position: 'fixed',
            display: 'flex',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={100} />
        </div>
      )}
      <DialogContent>
        <Grid item xs={12}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>EOC-Group#</TableCell>
                <TableCell>Ins Company</TableCell>
                <TableCell>Place of Service</TableCell>
                <TableCell>CPT</TableCell>
                <TableCell>Modifiers</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Date Paid</TableCell>
                <TableCell align="right">Amount Paid</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>{mapLogGridData()}</TableBody>
          </Table>
          {totalItems > 0 && (
            <div className="pagination-row">
              {paginationInfo()}
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                onPageChange={handlePageChange}
                forcePage={page - 1}
                pageCount={pageCount}
                containerClassName={'pagination'}
                activeClassName={'active'}
                renderOnZeroPageCount={undefined}
              />
            </div>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default BillOutHistoryModal
